<script setup>
import { computed, inject } from 'vue';
import Lightbox from '@/Components/Lightbox.vue';
import { activityChipStyle, formatActivityTime, toneStyle } from '@/lib/hubActivity.js';
import { useLiveActivity } from '../liveActivity.js';
import { styleVars } from './shared.js';
import { liveEmptyMessage } from '../blockContent.js';

// Live from the trails: the hub's "Activity today" on the org's own
// site — crew posts, status changes and weather, newest first.
const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// The empty line is the owner's (blockContent.js#liveEmptyMessage): a
// quiet day is news, unless they cleared it, and then a quiet day is
// no block at all, heading included (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const emptyMessage = computed(() => liveEmptyMessage(props.config, 'Nothing posted from the trails yet today.'));

const { shown, hiddenCount, showAll, lightboxImages, lightboxIndex, openPhoto } = useLiveActivity(props);
</script>

<template>
    <section v-if="shown.length || emptyMessage || editing" :id="config.anchor_id || null" class="th-block th-live th-sec" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.heading || config.summary" class="th-live__head">
                <h2 v-if="config.heading" class="th-h2 th-h2--sm">{{ config.heading }}</h2>
                <span v-if="config.summary" class="th-mono th-live__summary">{{ config.summary }}</span>
            </div>
            <div v-if="shown.length" class="th-panel th-live__list">
                <div v-for="a in shown" :key="a.key" class="th-live__row">
                    <div class="th-live__chip" :style="activityChipStyle(a.tone)">
                        <svg v-if="a.tone === 'weather'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 18a5 5 0 110-10 6 6 0 0111.7 1.5A4 4 0 0117 18H7z"/></svg>
                        <template v-else>{{ a.initial }}</template>
                    </div>
                    <div class="th-live__main">
                        <div class="th-live__meta">
                            <span class="th-live__kind" :style="activityChipStyle(a.tone)">{{ a.kind }}</span>
                            <span class="th-live__who">{{ a.who }}</span>
                            <span v-if="a.where" class="th-live__where">· {{ a.where }}</span>
                            <span v-if="a.pending" class="th-live__kind" :style="toneStyle('caution')">Pending review</span>
                        </div>
                        <div v-if="a.text" class="th-live__text">{{ a.text }}</div>
                        <div v-if="a.post && (a.post.image_urls || []).length" class="th-live__photos">
                            <button v-for="(url, i) in a.post.image_urls" :key="url" type="button"
                                    aria-label="Open photo" @click="openPhoto(a.post, i)">
                                <img :src="url" alt="" loading="lazy" />
                            </button>
                        </div>
                    </div>
                    <span class="th-mono th-live__time">{{ formatActivityTime(a.ts) }}</span>
                </div>
                <button v-if="hiddenCount" type="button" class="th-live__more" @click="showAll = true">Show {{ hiddenCount }} more</button>
            </div>
            <div v-else class="th-card th-live__empty">{{ emptyMessage || 'Nothing posted from the trails yet today.' }}</div>
        </div>
        <!-- Mounted only while open: it teleports to <body>, and a
             teleport in the server-rendered page fails hydration and
             empties the whole page (resources/js/ssr.js). -->
        <Lightbox v-if="lightboxIndex !== null" :images="lightboxImages" :index="lightboxIndex" @close="lightboxIndex = null" />
    </section>
</template>

<style scoped>
.th-live__head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 16px;
}
.th-live__summary { text-transform: uppercase; }
.th-live__list { display: flex; flex-direction: column; }
.th-live__row {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) auto;
    gap: 14px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--th-line-soft);
}
.th-live__row:last-of-type { border-bottom: 0; }
.th-live__chip {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 13px;
}
.th-live__main { min-width: 0; }
.th-live__meta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; }
.th-live__kind {
    font: 500 11px/1.2 var(--th-mono);
    letter-spacing: .05em; text-transform: uppercase;
    border-radius: 999px; padding: 4px 9px;
}
.th-live__who { font-weight: 700; }
.th-live__where { font-size: 15px; color: var(--th-muted); }
.th-live__text { margin-top: 4px; font-size: 15px; color: var(--th-muted); white-space: pre-wrap; overflow-wrap: anywhere; }
.th-live__time { font-size: 11px; text-transform: uppercase; white-space: nowrap; padding-top: 4px; }
.th-live__photos { margin-top: 10px; display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 8px; }
.th-live__photos button { padding: 0; border: 0; aspect-ratio: 1; border-radius: var(--r); overflow: hidden; cursor: pointer; }
.th-live__photos img { width: 100%; height: 100%; object-fit: cover; display: block; }
.th-live__more { border: 0; border-top: 1px solid var(--th-line-soft); background: #faf9f6; color: var(--th-ink); padding: 14px 20px; font-family: inherit; font-weight: 600; font-size: 15px; line-height: 1; cursor: pointer; text-align: center; }
.th-live__more:hover { background: #f1efe9; }
.th-live__more:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.th-live__empty { color: var(--th-muted); }
@media (max-width: 560px) {
    .th-live__row { grid-template-columns: 32px minmax(0, 1fr); gap: 4px 12px; padding: 14px 16px; }
    .th-live__chip { width: 32px; height: 32px; }
    /* The time drops under the words rather than squeezing them. */
    .th-live__time { grid-column: 2; padding-top: 2px; }
}
</style>
